import { Observer } from 'mobx-react-lite'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import User from 'singletons/User'

import { ProviderFormSchema } from 'schemas/Provider'

import AccountGeneralFormContent from './AccountGeneralFormContent'
import AccountProfileFormContent from './AccountProfileFormContent'
import { useMemo } from 'react'

type EditPageProps = {
  onClose: () => void
  currentSection: string | null
}

const EditPage = ({ onClose, currentSection }: EditPageProps) => {
  /* check for empty provider is done up in the components tree */
  const { provider } = User

  const {
    email,
    preferredAddress,
    notifications,
    preferredPhone,
    profile,
    profileDiagnosticExpertise,
    profileCommunityExpertise,
    profileReligiousExpertise,
  } = provider

  const { street1, street2, city, state, zip } = preferredAddress || {}

  const hookFormMethods = useForm({
    resolver: yupResolver(ProviderFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      street1,
      street2,
      city,
      state,
      zip,
      phoneNumber: preferredPhone?.number,
      phoneType: preferredPhone?.type,
      profile: {
        ...profile,
        diagnosticExpertise: profileDiagnosticExpertise,
        communityExpertise: profileCommunityExpertise,
        religiousExpertise: profileReligiousExpertise,
      },
      acceptSmsNotifications: notifications.sms,
      acceptEmailNotifications: notifications.email,
    },
  })

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = hookFormMethods

  const submitHandler = async (values) => {
    values.phoneNumber = values.phoneNumber.replaceAll(/[()-\s]/g, '')
    await provider.updateProvider({
      email,
      ...values,
    })
    onClose?.()
  }

  const FormContent = useMemo(() => {
    switch (currentSection) {
      case 'general':
        return AccountGeneralFormContent

      case 'profile':
        return AccountProfileFormContent

      default:
        return null
    }
  }, [currentSection])

  return (
    <FormProvider {...hookFormMethods}>
      <form className="w-full max-w-[496px] pt-4 pb-10" onSubmit={handleSubmit(submitHandler)}>
        <Observer>{() => !!FormContent && <FormContent />}</Observer>
        <div className="flex items-center justify-end gap-4 mt-10">
          <button type="button" className="btn-md btn-primary-outlined" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-md btn-primary" disabled={isSubmitting || !isDirty}>
            Save changes
          </button>
        </div>
      </form>
    </FormProvider>
  )
}

export default EditPage
